import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ROUTES } from '../constants';
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';
import Loader from '../components/Loader';

const Landing = lazy(() => import('../pages/Landing'));
const Login = lazy(() => import('../pages/Login'));
const Signup = lazy(() => import('../pages/Signup'));
const ForgotPassword = lazy(() => import('../pages/ForgotPassword'));
const SignupHorseOwner = lazy(() => import('../pages/SignupHorseOwner'));
const SignupRider = lazy(() => import('../pages/SignupRider'));
const HorseOwnerHome = lazy(() => import('../pages/HorseOwnerHome'));
const ProfilePage = lazy(() => import('../pages/horse-owner/ProfilePage'));
const MyHorsesPage = lazy(() => import('../pages/horse-owner/MyHorsesPage'));
const AddHorsePage = lazy(() => import('../pages/horse-owner/AddHorsePage'));
const HorseDetailPage = lazy(() => import('../pages/horse-owner/HorseDetailPage'));
const RiderWelcome = lazy(() => import('../pages/RiderWelcome'));
const HorseRiderHome = lazy(() => import('../pages/HorseRiderHome'));
const RiderHomePage = lazy(() => import('../pages/horse-rider/RiderHomePage'));
const BrowseHorsesPage = lazy(() => import('../pages/horse-rider/BrowseHorsesPage'));
const RiderHorseDetailPage = lazy(() => import('../pages/horse-rider/RiderHorseDetailPage'));
const MyEoisPage = lazy(() => import('../pages/horse-rider/MyEoisPage'));
const RiderProfilesPage = lazy(() => import('../pages/horse-rider/RiderProfilesPage'));
const NewRiderProfilePage = lazy(() => import('../pages/horse-rider/NewRiderProfilePage'));
const EditRiderProfilePage = lazy(() => import('../pages/horse-rider/EditRiderProfilePage'));
const BookingRequestsPage = lazy(() => import('../pages/BookingRequestsPage'));
const Home = lazy(() => import('../pages/Home'));
const AdminApprovalPage = lazy(() => import('../pages/AdminApprovalPage'));

// Marketing site (public)
const MarketingLanding = lazy(() => import('../pages/marketing/MarketingLanding'));
const WhoDoesWhat = lazy(() => import('../pages/marketing/WhoDoesWhat'));
const LeaseOrBuy = lazy(() => import('../pages/marketing/LeaseOrBuy'));

function SuspenseWrapper({ children, theme }: { children: React.ReactNode; theme?: 'owner' | 'rider' }) {
  return (
    <Suspense fallback={<Loader theme={theme} />}>
      {children}
    </Suspense>
  );
}

export const router = createBrowserRouter([
  // Marketing site — fully public, no auth guard (visible to everyone)
  { path: ROUTES.LANDING, element: <SuspenseWrapper><MarketingLanding /></SuspenseWrapper> },
  { path: ROUTES.GUIDE_WHO_DOES_WHAT, element: <SuspenseWrapper><WhoDoesWhat /></SuspenseWrapper> },
  { path: ROUTES.GUIDE_LEASE_OR_BUY, element: <SuspenseWrapper><LeaseOrBuy /></SuspenseWrapper> },

  // Public app routes — auth pages + app landing, redirect to home if already logged in
  {
    element: <PublicRoute />,
    children: [
      { path: ROUTES.APP_HOME, element: <SuspenseWrapper><Landing /></SuspenseWrapper> },
      { path: ROUTES.LOGIN, element: <SuspenseWrapper><Login /></SuspenseWrapper> },
      { path: ROUTES.FORGOT_PASSWORD, element: <SuspenseWrapper><ForgotPassword /></SuspenseWrapper> },
      { path: ROUTES.SIGNUP, element: <SuspenseWrapper><Signup /></SuspenseWrapper> },
      { path: ROUTES.SIGNUP_HORSE_OWNER, element: <SuspenseWrapper><SignupHorseOwner /></SuspenseWrapper> },
      { path: ROUTES.SIGNUP_RIDER, element: <SuspenseWrapper><SignupRider /></SuspenseWrapper> },
      { path: ROUTES.RIDER_WELCOME, element: <SuspenseWrapper theme="rider"><RiderWelcome /></SuspenseWrapper> },
    ],
  },
  // Private routes — redirect to login if not authenticated
  {
    element: <PrivateRoute />,
    children: [
      { path: ROUTES.DASHBOARD, element: <SuspenseWrapper><Home /></SuspenseWrapper> },
      {
        path: ROUTES.HORSE_OWNER_HOME,
        element: <SuspenseWrapper><HorseOwnerHome /></SuspenseWrapper>,
        children: [
          { path: 'profile', element: <Suspense fallback={null}><ProfilePage /></Suspense> },
          { path: 'my-horses', element: <Suspense fallback={null}><MyHorsesPage /></Suspense> },
          { path: 'my-horses/:horseId', element: <Suspense fallback={null}><HorseDetailPage /></Suspense> },
          { path: 'add-a-horse', element: <Suspense fallback={null}><AddHorsePage /></Suspense> },
        ],
      },
      {
        path: ROUTES.HORSE_RIDER_HOME,
        element: <SuspenseWrapper theme="rider"><HorseRiderHome /></SuspenseWrapper>,
        children: [
          { index: true, element: <Suspense fallback={null}><RiderHomePage /></Suspense> },
          { path: 'browse-horses', element: <Suspense fallback={null}><BrowseHorsesPage /></Suspense> },
          { path: 'browse-horses/:horseId', element: <Suspense fallback={null}><RiderHorseDetailPage /></Suspense> },
          { path: 'my-eois', element: <Suspense fallback={null}><MyEoisPage /></Suspense> },
          { path: 'rider-profiles', element: <Suspense fallback={null}><RiderProfilesPage /></Suspense> },
          { path: 'rider-profiles/new', element: <Suspense fallback={null}><NewRiderProfilePage /></Suspense> },
          { path: 'rider-profiles/:profileId/edit', element: <Suspense fallback={null}><EditRiderProfilePage /></Suspense> },
        ],
      },
      { path: ROUTES.BOOKING_REQUESTS, element: <SuspenseWrapper><BookingRequestsPage /></SuspenseWrapper> },
      { path: ROUTES.ADMIN_APPROVAL, element: <SuspenseWrapper><AdminApprovalPage /></SuspenseWrapper> },
    ],
  },
  // Catch-all: redirect undefined routes to the marketing home
  {
    path: '*',
    element: <Navigate to={ROUTES.LANDING} replace />,
  },
]);
